import { useEffect, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import axios from "axios";
import useCanonical from "@/hooks/useCanonical";
import { ReviewCard, ReviewsAggregate } from "@/components/ReaderReviews";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOK_COVER_URL =
  "/the-cage-cover.webp";

const PAGE_TITLE = "THE CAGE · Why Traders Destroy What They Build | The Biology Trader";
const PAGE_DESC =
  "A free, complete book on structural trading psychology: why a working edge survives analysis and fails under pressure, and the containment system built against it.";

const CONTENT_ROWS = [
  {
    title: "The spiral is mechanical",
    body:
      "A spiral is not one unusually bad trade. It is a repeating sequence: progress creates pressure, pressure changes size, one loss threatens the story, and the recovery attempt becomes the real damage. The book maps that sequence and the exits that disappear as it develops.",
  },
  {
    title: "Size changes the operator",
    body:
      "Position size does more than increase financial risk. At the wrong size, stops become negotiations, winners become urgent, decision speed slows, and every trade becomes personally important. Correct size allows the edge you already have to remain executable.",
  },
  {
    title: "Biology is a trading variable",
    body:
      "Sleep, physical exhaustion, external conflict, urgency and accumulated pressure can change execution before the first trade begins. State is not an excuse. It is information that must change what the account is permitted to do.",
  },
  {
    title: "Structure replaces willpower",
    body:
      "The cage is a set of limits put in place before pressure arrives: permitted size, trade count, daily loss, session close, instrument restrictions and friction around capital. Its purpose is not to make you more disciplined. It is to remove the decisions your pressured version cannot safely make.",
  },
  {
    title: "Consistency means survivability",
    body:
      "A clean losing day can be more valuable than a profitable rule violation. The book separates outcome from execution and shows why consistency is the ability to stay inside your own limits long enough for a working edge to compound.",
  },
  {
    title: "The cage must stay built",
    body:
      "The stressed trader is not the only threat. After several calm months, the builder begins removing walls because they appear unnecessary. Building the cage can take a weekend. Keeping it built is the continuing work.",
  },
];

const CHAPTERS = [
  "The Spiral Is Not Your Enemy. You Are",
  "The Ego Tax",
  "Biology Doesn't Care About Your Rules",
  "The False Summit",
  "Building the Cage",
  "Size Is the Variable You Refuse to Control",
  "Environment Is the Rule",
  "The Second Project",
  "What Consistency Actually Looks Like",
  "The Day the Spirals Died",
  "While This Book Was Being Written",
];

const CHAPTER_BULLETS = [
  "Drawn from nine years of trading",
  "Built from 168 timestamped journal entries",
  "Real sessions, losses, and rule violations, reported first-hand",
  "Delivered as a mobile-optimized edition formatted for comfortable reading on a phone",
  "The architecture the Cage System is built on",
];

const INSIDE_THE_CAGE = [
  "Why you keep trading while you watch yourself lose",
  "The 8 stages of a spiral, ranked by what each one costs you",
  "What breaks discipline under pressure, and the structure that replaces it",
  "The 5-question scorecard for the weeks when P&L lies",
  "How blowing your account becomes structurally impossible",
  "Why your worst trades are decided before the market opens",
];

// In-page smooth scroll to the email-delivery anchor.
function scrollToEmailForm(e) {
  e.preventDefault();
  const el = document.getElementById("email-delivery");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function ReviewWallSection() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/reviews`)
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, []);
  if (!data || data.total_count === 0) return null;
  return (
    <section className="border-b border-hairline" data-testid="ebook-reviews">
      <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32">
        <h2 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight text-center">
          Reader reviews
        </h2>
        <p className="mt-4 text-sm text-neutral-500 text-center" data-testid="ebook-reviews-moderation-note">
          Reviews are moderated for spam, abuse, and personal information.
        </p>
        {data.total_count >= 5 && (
          <ReviewsAggregate avg={data.average_rating} total={data.total_count} className="mt-6" />
        )}
        <div className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="ebook-reviews-grid">
          <div
            className="border border-neutral-200 bg-white p-6 flex flex-col items-center justify-center text-center"
            style={{ fontFamily: "'Inter Tight', ui-sans-serif, system-ui, sans-serif" }}
            data-testid="ebook-reviews-leave-review-card"
          >
            <p className="text-base font-semibold">Read the book?</p>
            <Link
              to="/leave"
              className="mt-2 text-base font-semibold underline underline-offset-4 decoration-2 hover:text-neutral-600"
              style={{ textDecorationColor: "#69d2c7" }}
              data-testid="ebook-reviews-leave-review-link"
            >
              Leave an honest review →
            </Link>
          </div>
          {data.reviews.map((r, i) => (
            <ReviewCard key={i} r={r} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function GetBookSection({ withAnchor = false }) {
  return (
    <section
      id={withAnchor ? "email-delivery" : undefined}
      className="scroll-mt-16"
      data-testid={withAnchor ? "ebook-email-section" : "ebook-email-section-bottom"}
    >
      <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
        <div className="md:col-span-6">
          <h2 className="font-display text-4xl md:text-5xl lg:text-[3.25rem] leading-[1.05] tracking-tight">
            Get the book.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-muted leading-[1.5] md:leading-[1.85] max-w-xl">
            Start with the diagnosis that changed everything. After years of losing control, it became the foundation of consistent profitability.
          </p>
          <p className="mt-8 label-mono text-muted text-[10px]" data-testid={withAnchor ? "ebook-closing-small" : "ebook-closing-small-bottom"}>
            125 pages · free · delivered by email
          </p>
        </div>
        <div className="md:col-span-6 md:col-start-7 w-full">
          <EmailDeliveryForm />
        </div>
      </div>
    </section>
  );
}

function StickyFreeBookBar() {
  const [pastHero, setPastHero] = useState(false);
  const [formInView, setFormInView] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    const hero = document.getElementById("ebook-hero");
    if (!hero) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => setPastHero(!entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(hero);
    const form = document.getElementById("email-delivery");
    let formObs;
    if (form) {
      formObs = new IntersectionObserver(
        ([entry]) => setFormInView(entry.isIntersecting),
        { threshold: 0 }
      );
      formObs.observe(form);
    }
    return () => {
      obs.disconnect();
      if (formObs) formObs.disconnect();
    };
  }, []);
  if (!pastHero || dismissed || formInView) return null;
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 bg-paper/95 backdrop-blur-md border-t border-hairline shadow-[0_-8px_30px_rgba(10,10,10,0.04)]"
      data-testid="sticky-get-free-book"
    >
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="absolute top-3 right-4 md:top-4 md:right-6 p-1.5 text-muted hover:text-ink transition-colors"
        data-testid="sticky-get-free-book-close"
      >
        <X size={18} strokeWidth={1.5} />
      </button>

      <a
        href="#email-delivery"
        onClick={scrollToEmailForm}
        className="group block w-full text-left cursor-pointer"
        data-testid="sticky-get-free-book-link"
      >
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-6 md:py-7 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-10 items-center">
          <div className="md:col-span-2 label-mono text-muted">The book</div>
          <h2 className="md:col-span-8 font-display text-xl sm:text-2xl md:text-[1.75rem] lg:text-3xl leading-[1.15] tracking-tight text-ink group-hover:text-leaf transition-colors">
            GET THE FREE BOOK
          </h2>
          <div className="md:col-span-2 md:text-right label-mono text-muted group-hover:text-leaf transition-colors flex items-center md:justify-end gap-2">
            Get it now
            <ArrowRight size={14} strokeWidth={1.75} />
          </div>
        </div>
      </a>
    </div>
  );
}

function EmailDeliveryForm() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [downloadToken, setDownloadToken] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      setError("Please fill in both fields.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/free-book`, {
        first_name: firstName.trim(),
        email: email.trim(),
        src: "ebook",
      });
      try {
        sessionStorage.setItem("bt_lead_email", email.trim());
        sessionStorage.setItem("bt_download_token", res.data.download_token || "");
      } catch {
        /* sessionStorage may be unavailable */
      }
      setDownloadToken(res.data.download_token || "");
      setSent(true);
    } catch {
      setError("Something went wrong. Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="border border-hairline bg-white p-6 sm:p-8 lg:px-10 lg:py-9" data-testid="ebook-form-success">
        <p className="label-mono text-muted">Sent</p>
        <h3 className="font-display mt-3 text-2xl md:text-3xl leading-tight">
          Check your inbox. The book is on its way.
        </h3>
        <p className="mt-4 text-muted leading-[1.5] md:leading-[1.7]">
          You&rsquo;ll also receive the weekly journal email. In the meantime, you can start reading right now.
        </p>
        <a
          href={`${API}/download/${downloadToken || "unknown"}/THE-CAGE-The-Biology-Trader.pdf?via=ebook`}
          download="THE-CAGE-The-Biology-Trader.pdf"
          type="application/pdf"
          className="group inline-flex items-center gap-3 bg-ink text-white px-8 py-4 label-mono hover:bg-leaf transition-colors mt-8"
          data-testid="ebook-success-read-button"
        >
          Read the complete book →
        </a>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="border border-hairline bg-white p-6 sm:p-8 lg:px-10 lg:py-9"
      data-testid="ebook-email-form"
      noValidate
    >
      {/* Honeypot for basic bot filtering — invisible to humans */}
      <input
        type="text"
        name="website"
        tabIndex="-1"
        autoComplete="off"
        className="absolute left-[-9999px] top-auto w-px h-px overflow-hidden"
        aria-hidden="true"
      />
      {/* Header treatment mirroring the /free-book hero + form title block */}
      <div className="text-center mb-6" data-testid="ebook-form-header">
        <p className="text-xs font-bold tracking-[0.22em] uppercase">
          <span className="inline-block px-2 py-1 text-white" style={{ backgroundColor: "#22c55e" }}>Free Book</span>
          <span className="text-neutral-400 whitespace-nowrap"> by The Biology Trader</span>
        </p>
        <p className="mt-4 text-xl font-bold text-neutral-950">The Cage</p>
        <p className="mt-1 text-sm font-bold tracking-[0.22em] uppercase text-neutral-400">The Full Book</p>
      </div>
      <div>
        <label htmlFor="ebook-name" className="block text-left text-sm font-semibold mb-1.5">
          Name
        </label>
        <input
          id="ebook-name"
          type="text"
          required
          autoComplete="name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Name"
          data-testid="ebook-first-name-input"
          className="w-full border border-neutral-300 focus:border-neutral-900 outline-none text-base px-4 py-3 placeholder:text-neutral-400"
        />
      </div>
      <div className="mt-3">
        <label htmlFor="ebook-email" className="block text-left text-sm font-semibold mb-1.5">
          Email
        </label>
        <input
          id="ebook-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          data-testid="ebook-email-input"
          className="w-full border border-neutral-300 focus:border-neutral-900 outline-none text-base px-4 py-3 placeholder:text-neutral-400"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="ebook-submit-button"
        className="mt-5 w-full text-lg font-extrabold py-4 px-8 bg-neutral-950 text-white hover:bg-neutral-800 disabled:opacity-60 transition-colors"
      >
        {loading ? "Sending…" : "Send me the FREE book"}
      </button>
      {error && (
        <p className="mt-3 text-sm text-red-600" data-testid="ebook-form-error" role="alert">
          {error}
        </p>
      )}
      <p className="mt-4 text-sm text-muted text-center">
        Instant delivery&nbsp;·&nbsp;Occasional updates
      </p>
      <p className="mt-1 text-sm text-center" style={{ color: "#16a34a" }}>
        Nothing hidden. You fill in the form, you get the book.
      </p>
    </form>
  );
}

export default function Ebook() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-paper text-ink" data-testid="page-ebook">
      {/* ---------- 1. HERO ---------- */}
      <section id="ebook-hero" className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16 md:pb-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
            <div className="md:col-span-5 order-2 md:order-none" data-testid="ebook-cover">
              <img
                src={BOOK_COVER_URL}
                alt="THE CAGE book cover"
                className="block w-full max-w-sm h-auto"
                data-testid="ebook-cover-img"
              />
            </div>
            <div className="md:col-span-7 order-1 md:order-none">
              <p className="label-mono text-muted" data-testid="ebook-eyebrow">The book</p>
              <h1 className="font-display mt-6 text-[3rem] sm:text-6xl md:text-7xl lg:text-[6rem] leading-[0.95] tracking-tight" data-testid="ebook-headline">
                THE CAGE.
                <span className="block mt-3 text-lg md:text-2xl font-display-medium leading-tight tracking-tight text-muted">A Book for Traders Who Break Their Own Rules</span>
              </h1>
              <h2 className="font-display-medium mt-6 text-2xl md:text-4xl leading-tight max-w-3xl" data-testid="ebook-subheadline">
                Why traders destroy what they build.
              </h2>
              <p className="mt-8 text-lg md:text-xl text-muted leading-[1.5] md:leading-[1.7] max-w-xl">
                A complete book on structural trading psychology, drawn from nine years of trading and 168 journal entries.
              </p>
              <div className="mt-10">
                <a
                  href="#email-delivery"
                  onClick={scrollToEmailForm}
                  className="group inline-flex items-center gap-3 bg-ink text-white px-8 py-4 label-mono hover:bg-leaf transition-colors"
                  data-testid="ebook-hero-email-link"
                >
                  Send me the book →
                </a>
              </div>
              <p className="mt-3 label-mono text-muted text-[10px]" data-testid="ebook-hero-small">
                125 pages · free · delivered by email
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 2. CORE RECOGNITION ---------- */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-6">
            <h2 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight">
              You know what to do.
            </h2>
            <p className="mt-4 text-xl md:text-2xl font-bold text-neutral-400 leading-snug max-w-md">
              Under pressure, you become someone who cannot do it.
            </p>
          </div>
          <div className="md:col-span-6 text-lg md:text-xl text-muted leading-[1.5] md:leading-[1.85] space-y-6 max-w-2xl">
            <p>
              This book is for the trader who can read the market, has experienced profitable periods, and still watches weeks of progress disappear through oversizing, revenge trading, urgency, or one session that will not end.
            </p>
            <p>
              The problem is not always missing knowledge. It is what happens when pressure changes the person executing it.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- 3. INSIDE THE CAGE ---------- */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-6">
            <h2 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight">
              Inside THE CAGE.
            </h2>
          </div>
          <div className="md:col-span-6">
            <ul className="space-y-6 max-w-2xl" data-testid="ebook-audience-list">
              {INSIDE_THE_CAGE.map((item) => (
                <li key={item} className="flex items-start gap-4 text-lg md:text-xl text-ink leading-relaxed">
                  <span
                    className="w-6 h-6 shrink-0 mt-1 flex items-center justify-center text-xs font-bold text-white rounded-full"
                    style={{ backgroundColor: "#22c55e" }}
                    aria-hidden="true"
                  >
                    ✓
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-14 text-lg md:text-xl text-muted max-w-2xl">
              Eleven chapters, plus a preface, conclusion, and implementation roadmap.
            </p>
            <ol className="mt-8 divide-y divide-hairline border-t border-hairline max-w-3xl" data-testid="ebook-chapters">
              {CHAPTERS.map((title, i) => (
                <li key={title} className="py-5 grid grid-cols-12 gap-4 items-baseline">
                  <span className="col-span-2 md:col-span-1 label-mono text-muted">{i + 1}</span>
                  <span className="col-span-10 md:col-span-11 text-lg md:text-xl text-ink">{title}</span>
                </li>
              ))}
              <li className="py-5 grid grid-cols-12 gap-4 items-baseline">
                <span className="col-span-2 md:col-span-1" aria-hidden="true"></span>
                <span className="col-span-10 md:col-span-11 text-lg md:text-xl text-muted" data-testid="ebook-chapters-note">The chapter I had every reason to hide.</span>
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* ---------- 4. CONTENT ROWS ---------- */}
      {CONTENT_ROWS.map((row) => (
        <section key={row.title} className="border-b border-hairline">
          <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
            <h3 className="md:col-span-4 section-label" data-testid={`ebook-row-title-${row.title.toLowerCase().replace(/\s+/g, "-")}`}>
              {row.title}
            </h3>
            <p className="md:col-span-7 md:col-start-6 text-lg md:text-xl text-muted leading-[1.5] md:leading-[1.85] max-w-2xl">
              {row.body}
            </p>
          </div>
        </section>
      ))}

      {/* ---------- 5. READER REVIEWS ---------- */}
      <ReviewWallSection />

      {/* ---------- 6. EMAIL DELIVERY — Get the book, last on the page ---------- */}
      <GetBookSection withAnchor />
      <StickyFreeBookBar />
    </div>
  );
}
